import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import toast from 'components/partials/toast/toast';
import { getApiErrorDetail } from 'helpers/api/get-api-error-detail';

import useAwardCalculationDraft from './use-award-calculation-draft';
import { usePopulationsDraft } from './use-populations-draft';

import { POPULATION_TYPES } from '../matrix-step.constants';

import { useUpdateRegime } from '../../../hooks/use-aid-regimes';

import {
  AWARD_TYPE_OPTIONS,
  CALCULATED_BY_OPTIONS,
  buildRangeKey,
  getUniqueRangesFromCalculation,
  isPellRange,
  sortRangesForDisplay,
} from '../definition-tab.constants';

import type {
  AwardType,
  CalculatedBy,
  Range,
} from '../definition-tab.constants';

type RangesByType = Record<string, Range[]>;

const EMPTY_RANGES_BY_TYPE: RangesByType = AWARD_TYPE_OPTIONS.reduce(
  (acc, option) => ({ ...acc, [option.value]: [] }),
  {} as RangesByType
);

const serializeRanges = (r: Range[]): string => r.map(buildRangeKey).join('|');

const serializeRangesByType = (rangesByType: RangesByType): string =>
  AWARD_TYPE_OPTIONS.map(
    (option) =>
      `${option.value}:${serializeRanges(rangesByType[option.value] ?? [])}`
  ).join(';');

interface UseRegimeDraftParams {
  selectedRegime?: Record<string, unknown> | null;
  academicRanks?: unknown[];
  customLogicCategories?: unknown[];
  userId?: string | null;
  modelId?: string | number;
  onSaveSuccess?: () => void;
}

const useRegimeDraft = ({
  selectedRegime,
  academicRanks,
  customLogicCategories,
  userId,
  modelId,
  onSaveSuccess,
}: UseRegimeDraftParams) => {
  const [awardType, setAwardType] = useState(AWARD_TYPE_OPTIONS[0].value);
  const [calculatedBy, setCalculatedBy] = useState(
    CALCULATED_BY_OPTIONS[0].value
  );
  const [rangesByType, setRangesByType] = useState(EMPTY_RANGES_BY_TYPE);
  const definitionInitRef = useRef({
    awardType: AWARD_TYPE_OPTIONS[0].value,
    calculatedBy: CALCULATED_BY_OPTIONS[0].value,
    rangesByType: EMPTY_RANGES_BY_TYPE,
  });

  useEffect(() => {
    const nextAwardType = (selectedRegime?.awardType ??
      AWARD_TYPE_OPTIONS[0].value) as AwardType;
    const nextCalculatedBy = (selectedRegime?.calculatedBy ??
      CALCULATED_BY_OPTIONS[0].value) as CalculatedBy;
    const extracted = getUniqueRangesFromCalculation(
      selectedRegime?.calculation as never
    );
    // A Pell regime's single input column always exists conceptually, even
    // before the backend has ever persisted a calculation cell for it (e.g.
    // a regime created with no sibling to seed from). Synthesize it here so
    // Definition/Award Calculation don't fall back to their "no ranges yet"
    // empty states for a regime that isn't actually missing anything.
    const rangesWithPellPlaceholder =
      selectedRegime?.pell && !extracted.some(isPellRange)
        ? [...extracted, { from: null, to: null, nonAid: false }]
        : extracted;
    const nextRanges = sortRangesForDisplay(rangesWithPellPlaceholder);
    // A Pell regime's ranges are just the Pell cell (plus, structurally, any
    // stray Non-Aid one) — they aren't bucketed per award type like a
    // standard regime's ranges are. Seed every award-type bucket with the
    // same set, so switching "Award Need based funds using" between
    // Need/AGI/SAI doesn't make the Pell Recipients column disappear.
    const nextRangesByType: RangesByType = selectedRegime?.pell
      ? AWARD_TYPE_OPTIONS.reduce(
          (acc, option) => ({ ...acc, [option.value]: nextRanges }),
          {} as RangesByType
        )
      : { ...EMPTY_RANGES_BY_TYPE, [nextAwardType]: nextRanges };

    setAwardType(nextAwardType);
    setCalculatedBy(nextCalculatedBy);
    setRangesByType(nextRangesByType);
    definitionInitRef.current = {
      awardType: nextAwardType,
      calculatedBy: nextCalculatedBy,
      rangesByType: nextRangesByType,
    };
  }, [selectedRegime?.id]);

  const ranges = useMemo(
    () => rangesByType[awardType] ?? [],
    [rangesByType, awardType]
  );

  const handleAwardTypeChange = useCallback((value: string | undefined) => {
    if (value !== undefined) setAwardType(value as AwardType);
  }, []);

  const handleCalculatedByChange = useCallback((value: string | undefined) => {
    if (value !== undefined) setCalculatedBy(value as CalculatedBy);
  }, []);

  const handleRangesChange = useCallback(
    (updatedRanges: Range[]) => {
      setRangesByType((prev: RangesByType) => ({
        ...prev,
        [awardType]: updatedRanges,
      }));
    },
    [awardType]
  );

  const {
    rankIds,
    rankIdToDisplayNumber,
    getCell,
    updateCell,
    hasChanges: matrixHasChanges,
    resetDraft: resetMatrixDraft,
    toCalculationPayload,
    markAsSaved: markMatrixSaved,
  } = useAwardCalculationDraft({
    regime: selectedRegime as never,
    ranges: ranges as unknown as Record<string, unknown>[],
    academicRanks: academicRanks as unknown as Array<{
      id: string | number;
      rank: number;
    }>,
  });

  const {
    populationType,
    handlePopulationTypeChange,
    baselineSelected,
    toggleBaseline,
    criteriaBlocks,
    criteriaErrors,
    addCriteriaBlock,
    removeCriteriaBlock,
    canAddCriteriaBlock,
    handleBlockDimensionChange,
    handleBlockValuesChange,
    getDimensionOptionsForBlock,
    getValueOptions,
    customLogicIds: draftCustomLogicIds,
    pell,
    validatePopulations,
    hasChanges: populationsHasChanges,
    resetDraft: resetPopulationsDraft,
    markAsSaved: markPopulationsSaved,
  } = usePopulationsDraft({
    regime: selectedRegime as never,
    customLogicCategories: customLogicCategories as never,
  });

  const definitionHasChanges =
    awardType !== definitionInitRef.current.awardType ||
    calculatedBy !== definitionInitRef.current.calculatedBy ||
    serializeRangesByType(rangesByType) !==
      serializeRangesByType(definitionInitRef.current.rangesByType);

  const hasChanges =
    matrixHasChanges || definitionHasChanges || populationsHasChanges;

  const updateRegimeMutation = useUpdateRegime();
  const isSaving = updateRegimeMutation.isPending;

  const handleSave = useCallback(async () => {
    if (!selectedRegime || !userId || !modelId) return;
    if (!validatePopulations()) return;

    const data = {
      name: selectedRegime.name,
      population:
        populationType === POPULATION_TYPES.BASELINE ? baselineSelected : null,
      customLogicIds: draftCustomLogicIds,
      awardType,
      calculatedBy,
      pell,
      calculation: toCalculationPayload(),
      groupId: selectedRegime.groupId,
      sortOrder: selectedRegime.sortOrder,
    };

    try {
      await updateRegimeMutation.mutateAsync({
        userId,
        modelId,
        regimeId: selectedRegime.id as unknown as string | number,
        data,
      });

      markMatrixSaved();
      markPopulationsSaved();
      definitionInitRef.current = {
        awardType,
        calculatedBy,
        rangesByType,
      };
      toast.success({ title: 'Regime saved successfully' });
      onSaveSuccess?.();
    } catch (error) {
      toast.error({ title: getApiErrorDetail(error, 'Failed to save regime') });
    }
  }, [
    selectedRegime,
    userId,
    modelId,
    populationType,
    baselineSelected,
    draftCustomLogicIds,
    awardType,
    calculatedBy,
    pell,
    rangesByType,
    toCalculationPayload,
    updateRegimeMutation,
    markMatrixSaved,
    markPopulationsSaved,
    validatePopulations,
    onSaveSuccess,
  ]);

  const handleDiscard = useCallback(() => {
    setAwardType(definitionInitRef.current.awardType);
    setCalculatedBy(definitionInitRef.current.calculatedBy);
    setRangesByType(definitionInitRef.current.rangesByType);
    resetMatrixDraft();
    resetPopulationsDraft();
  }, [resetMatrixDraft, resetPopulationsDraft]);

  return {
    awardType,
    calculatedBy,
    ranges,
    onAwardTypeChange: handleAwardTypeChange,
    onCalculatedByChange: handleCalculatedByChange,
    onRangesChange: handleRangesChange,

    rankIds,
    rankIdToDisplayNumber,
    getCell,
    updateCell,

    populationType,
    handlePopulationTypeChange,
    baselineSelected,
    toggleBaseline,
    criteriaBlocks,
    criteriaErrors,
    addCriteriaBlock,
    removeCriteriaBlock,
    canAddCriteriaBlock,
    handleBlockDimensionChange,
    handleBlockValuesChange,
    getDimensionOptionsForBlock,
    getValueOptions,
    pell,
    hasChanges,
    isSaving,
    handleSave,
    handleDiscard,
  };
};

export default useRegimeDraft;
